import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { billingMe } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PaywallDialog from "@/components/PaywallDialog";
import { ADMIN_CAPS, capsFromLimits } from "@/lib/planCaps";

const GateContext = createContext(null);

const REASON_COPY = {
    ai: {
        title: "You've used your free AI words",
        body: "Enchanter, Magnus and Aza each include a monthly AI word allowance for drafting, editing and proofreading. Upgrade to keep your co-author at the desk.",
        minPlan: "Enchanter",
    },
    novels: {
        title: "You've reached the Seer novel limit",
        body: "The free Seer plan is capped at 3 novels. Upgrade to Enchanter for unlimited manuscripts.",
        minPlan: "Enchanter",
    },
    words: {
        title: "This novel has reached the Seer word limit",
        body: "Free Seer novels can hold up to 40,000 words. Upgrade to Enchanter for unlimited length. Your latest changes are kept on this device until then.",
        minPlan: "Enchanter",
    },
};

export function GateProvider({ children }) {
    const { user, loading: authLoading } = useAuth();
    const [me, setMe] = useState(null);
    const [ready, setReady] = useState(false);
    const [paywallReason, setPaywallReason] = useState(null);
    // Optional per-call copy, e.g. the server's own "you've used this
    // month's words" message, which differs by plan.
    const [paywallCopy, setPaywallCopy] = useState(null);

    const refresh = useCallback(async () => {
        if (!user) {
            setMe(null);
            setReady(true);
            return;
        }
        try {
            const data = await billingMe();
            setMe(data);
        } catch {
            setMe(null);
        } finally {
            setReady(true);
        }
    }, [user]);

    useEffect(() => {
        if (authLoading) return;
        setReady(false);
        refresh();
    }, [authLoading, refresh]);

    const value = useMemo(() => {
        const isAdmin = !!(me?.is_admin || user?.is_admin);
        const plan = me?.plan || "thoughts";
        // Derived from the limits the backend sends, never a local copy of them.
        const caps = capsFromLimits(me?.limits);
        // Admin bypasses every gate.
        const effective = isAdmin ? ADMIN_CAPS : caps;
        const billingEnabled = me?.billing_enabled !== false; // default true if unknown
        return {
            ready,
            me,
            plan,
            planLabel: me?.plan_label || caps.label,
            isAdmin,
            billingEnabled,
            canAI: isAdmin || !billingEnabled || effective.canAI,
            maxNovels: effective.maxNovels,
            canCreateNovel: (currentCount) =>
                isAdmin || !billingEnabled || currentCount < effective.maxNovels,
            openPaywall: (reason, copy) => { setPaywallCopy(copy || null); setPaywallReason(reason || "ai"); },
            refresh,
        };
    }, [me, user, ready, refresh]);

    return (
        <GateContext.Provider value={value}>
            {children}
            <PaywallDialog
                open={!!paywallReason}
                onClose={() => setPaywallReason(null)}
                reason={paywallReason}
                copy={paywallReason ? { ...REASON_COPY[paywallReason], ...(paywallCopy || {}) } : null}
            />
        </GateContext.Provider>
    );
}

export function useGate() {
    const ctx = useContext(GateContext);
    if (!ctx) {
        // Safe fallback when used outside provider (shouldn't happen)
        return {
            ready: false, isAdmin: false, plan: "thoughts", planLabel: "Free",
            billingEnabled: true, canAI: false, maxNovels: 3,
            canCreateNovel: () => false, openPaywall: () => {}, refresh: async () => {},
        };
    }
    return ctx;
}

export function usePaywallNav() {
    const nav = useNavigate();
    return () => nav("/pricing");
}
